import styled from '@emotion/styled';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Pagination,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import type { ColumnResizeMode, ExpandedState, SortingState } from '@tanstack/react-table';
import { createColumnHelper, flexRender, getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table';
import type { HTMLProps } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import classSet from 'react-classset';
import { useSelector } from 'react-redux';

import { Permission } from '../../enums';
import { FuelEntityType } from '../../services';
import type { DateRangeId } from '../../store/slices/asset-listing/DateRangeId';
import { fuelUserAccessSliceSelector } from '../../store/slices/fuelUserAccess';
import paginationUtils from '../../utils/paginationUtils';
import FuelAutoComplete from '../fuel-autocomplete';
import FuelSearchInput from '../fuel-search-input';
import FuelTableRows from '../fuel-table-rows/FuelTableRows';
// import BookmarkIcon from '@mui/icons-material/Bookmark';

const DEFAULT_PAGINATION = {
  sortBy: 'createdDate',
  descending: true,
  rowsPerPage: 100,
  page: 1,
  totalItems: 0,
  pages: 1,
};

const dateRangeOptions: Array<{ id: DateRangeId; name: string }> = [
  { id: '1-day', name: '+/- 1 Day' },
  { id: '3-days', name: '+/- 3 Days' },
  { id: '1-week', name: '+/- 1 Week' },
  { id: '2-weeks', name: '+/- 2 Weeks' },
  { id: '1-month', name: '+/- 1 Month' },
  { id: '2-months', name: '+/- 2 Months' },
  { id: '6-months', name: '+/- 6 Months' },
  { id: '1-year', name: '+/- 1 Year' },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  root: {
    '&:hover': {
      backgroundColor: 'red',
    },
  },
  [`&.${tableCellClasses.head}`]: {
    // backgroundColor: '#e1e1e1',
    // fontWeight: '100',
    // borderBottom: '1px solid rgba(0,0,0,.12)',
    // fontFamily: 'Roboto',
  },
  [`&.${tableCellClasses.body}`]: {
    // fontSize: 13,
    // color: '#696b6a',
    // fontWeight: '400',
    // fontFamily: 'Roboto',
  },
}));

function IndeterminateCheckbox({ indeterminate, ...rest }: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!);

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate]);

  // return <input type="checkbox" ref={ref} className={className + ' cursor-pointer'} {...rest} />;
  return <Checkbox {...rest} size="small" ref={ref} />;
}

interface Iprops {
  data: any[];
  columnHelper: any;
  defaultColumns: any[];
  paginationDetail?: any;
  paginationChange?: (pagination) => void;
  loading?: boolean;
  tableHeight?: number;
  showRowSelection?: boolean;
  hidePagination?: boolean;
  drop?: boolean;
  drag?: boolean;
  pull?: boolean;
  onDrag?: ((newItems: any[], lastDroppedElement: any) => void) | null;
  handleSelectedRows?: (items: any[]) => void;
  dropInPlace?: boolean;
  clearSelectedRow?: boolean;
  columnVisibiltyState?: any;
  showActionbar?: boolean;
  customAddButtonText?: any;
  customActionbarHtml?: any;
  onActionbarAction?: ((event: MouseEvent | null, type: string, data?: any) => void) | null;
  onSearch?: ((event: MouseEvent | null, type: string, data: any) => void) | null;
  searchDefaultValue?: string | null;
  hideAddButton?: boolean;
  hideThumbnailButtons?: boolean;
  hideSelectionButtons?: boolean;
  hideSearchControl?: boolean;
  showDateFilter?: boolean;
  hideActionbarButtons?: boolean;
  showExportButton?: boolean;
  defaultRangeValue?: any;
  defaultSelectedDate?: any;
  id?: string;
  entityType?: string;
  rowRefs?: any;
  dragAndDropText?: string;
}

const FuelTable = ({
  data = [],
  columnHelper = createColumnHelper<typeof data>(),
  defaultColumns = [],
  paginationDetail = DEFAULT_PAGINATION,
  paginationChange,
  showRowSelection = false,
  loading = false,
  tableHeight = 50,
  hidePagination = false,
  drop = false,
  drag = false,
  pull = false,
  onDrag,
  handleSelectedRows,
  dropInPlace = false,
  clearSelectedRow = false,
  columnVisibiltyState = {},
  showActionbar = false,
  customActionbarHtml = null,
  customAddButtonText = null,
  onActionbarAction = null,
  onSearch = null,
  searchDefaultValue = null,
  hideAddButton = false,
  hideThumbnailButtons = false,
  hideSelectionButtons = false,
  hideSearchControl = false,
  showDateFilter = false,
  hideActionbarButtons = false,
  showExportButton = false,
  defaultRangeValue = '',
  defaultSelectedDate = null,
  id = null,
  entityType = null,
  rowRefs = null,
  dragAndDropText = 'Drag and Drop here',
}: Iprops) => {
  const { permissions } = useSelector(fuelUserAccessSliceSelector);
  const [mySelectedRows, setMySelectedRows] = useState([]);
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  // // set defaults if no props provided
  if (hidePagination !== true) hidePagination = false;
  if (!data) data = [];
  if (!columnHelper) columnHelper = createColumnHelper<typeof data>();
  if (!defaultColumns) defaultColumns = [];
  if (!paginationDetail) paginationDetail = DEFAULT_PAGINATION;
  if (showRowSelection !== true) showRowSelection = false;
  if (!tableHeight) tableHeight = 50;

  const [fromDate, setFromDate] = useState(defaultSelectedDate);
  const [dateRange, setDateRange] = useState(defaultRangeValue);

  const tableContainerRef = useRef(null);
  const scrollTopBeforeLoading = useRef(0);

  // const [data, setData] = useState(() => [...defaultData])
  const checkboaxColumn = columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <IndeterminateCheckbox
        {...{
          checked: table.getIsAllRowsSelected(),
          indeterminate: table.getIsSomeRowsSelected(),
          onChange: table.getToggleAllRowsSelectedHandler(),
        }}
      />
    ),
    cell: ({ row }) => (
      <div className="px-1">
        {showCheckbox(row) && (
          <IndeterminateCheckbox
            {...{
              checked: row.getIsSelected(),
              indeterminate: row.getIsSomeSelected(),
              onChange: row.getToggleSelectedHandler(),
            }}
          />
        )}
      </div>
    ),
  });

  if (showRowSelection === true) {
    defaultColumns = [checkboaxColumn, ...defaultColumns];
  }

  const [columns, setColumns] = useState<typeof defaultColumns>(defaultColumns);

  const [rowSelection, setRowSelection] = useState({});

  if (!paginationDetail) {
    paginationDetail = DEFAULT_PAGINATION;
  }

  const [pagination, setPagination] = useState({
    pageIndex: paginationDetail.page,
    pageSize: paginationDetail.rowsPerPage,
  });

  const [paginationSummary, setPaginationSummary] = useState(
    paginationUtils.getPaginationSummary(paginationDetail, paginationDetail.totalItems)
  );

  let sortingState: any = [];
  if (paginationDetail && paginationDetail.sortBy && defaultColumns && defaultColumns.length > 0) {
    const found = defaultColumns.find((c: any) => c.accessorKey === paginationDetail.sortBy);
    if (found) {
      sortingState = [{ id: paginationDetail.sortBy, desc: paginationDetail.descending }];
    }
  }

  const [sorting, setSorting] = useState<SortingState>(sortingState);

  const [columnResizeMode, setColumnResizeMode] = useState<ColumnResizeMode>('onChange');

  const getTableCellWidth = (cell) => {
    if (cell.column.id === 'select') return cell.column.columnDef.minSize;
    return cell.column.columnDef.width ? cell.column.columnDef.width : cell.column.getSize();
  };

  const showCheckbox = (row) => {
    return showRowSelection && row.original.isSelectable;
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    const obj = pagination;
    obj.page = value;
    const paginationDetails = paginationDetail;
    setPagination(obj);
    paginationDetails.page = value;
    if (paginationChange) paginationChange(paginationDetails);
  };

  const onHeaderClick = (event: React.ChangeEvent<unknown>, column: any) => {
    const paginationObj = paginationDetail;
    if (column.columnDef.sortable) {
      if (paginationObj.sortBy === column.id) {
        paginationObj.descending = !paginationObj.descending;
      } else {
        paginationObj.sortBy = column.id;
        paginationObj.descending = false;
      }
      paginationObj.page = 1;
      const pinnedSorting = { id: 'isPinned', desc: false };
      const newSorting = [{ id: paginationObj.sortBy, desc: paginationObj.descending }];
      if (entityType === FuelEntityType.Channel) {
        setSorting([pinnedSorting, ...newSorting]);
      } else {
        setSorting(newSorting);
      }

      setPagination({
        pageIndex: paginationObj.page,
        pageSize: paginationObj.rowsPerPage,
      });
      if (paginationChange) paginationChange(paginationObj);
    }
  };
  // const [expanded, setExpanded] = React.useState<ExpandedState>({})

  const table = useReactTable({
    data,
    columns,
    state: {
      rowSelection,
      sorting,
      pagination,
      columnVisibility: columnVisibiltyState,
      expanded,
    },
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting, // Ignore sorting changes
    onPaginationChange: setPagination,
    columnResizeMode,
    getCoreRowModel: getCoreRowModel(),
    // getSortedRowModel: getSortedRowModel(),
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.children,
    getExpandedRowModel: getExpandedRowModel(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  // console.log('FuelTable', table);

  useEffect(() => {
    if (!paginationDetail) paginationDetail = DEFAULT_PAGINATION;
    if (clearSelectedRow) {
      setRowSelection({});
    }
    setPaginationSummary(paginationUtils.getPaginationSummary(paginationDetail, paginationDetail.totalItems));
  }, [data, paginationDetail]);

  // const selectedRowData = table.getSelectedRowModel().flatRows.map((row) => row.original);

  useEffect(() => {
    const selectedRowData = table.getSelectedRowModel().flatRows.map((row) => row.original);
    // console.log('selectedRowData', {selectedRowData, entityType});
    if (handleSelectedRows && showRowSelection) {
      handleSelectedRows(selectedRowData);
    }
    setMySelectedRows(selectedRowData);
    // handleSelectedRows(selectedRowData);
  }, [rowSelection]);

  useEffect(() => {
    // console.log('defaultColumns changed', { defaultColumns, columns });
    if (
      defaultColumns &&
      defaultColumns.length > 0 &&
      columns &&
      columns.length > 0 &&
      defaultColumns.length !== columns.length
    ) {
      setColumns(defaultColumns);
    }
  }, [defaultColumns]);

  // --------------------------------------------------
  //
  // TABLE ACTIONBAR
  //
  // --------------------------------------------------

  const onSearchTextChanged = (value) => {
    // console.log('onSearchTextChanged', value);
    let data = { searchText: value };
    if (showDateFilter) {
      data = { ...data, dateRange, fromDate };
    }
    if (onActionbarAction) onActionbarAction(null, 'search', data);
  };

  const onWordSearch = (value) => {
    // console.log('onWordSearch', value);
    let data = { searchText: value };
    if (showDateFilter) {
      data = { ...data, dateRange, fromDate };
    }
    if (onSearch) onSearch(null, 'search', data);
    if (onActionbarAction) onActionbarAction(null, 'search', data);

    // else {
    //   if (!value || value.trim().length === 0) {
    //     // TODO: Restore original list
    //   } else {
    //     // TODO: in memory text search
    //     const originalData = _.cloneDeep(data);
    //   }
    // }
  };

  const onDateInputChange = (field, value) => {
    // console.log(field, value);
    if (field === 'date') {
      setFromDate(value);
    } else if (field === 'dateRange') {
      setDateRange(value);
    }
  };

  const onCustomAddButtonClick = (evt) => {
    // console.log('onCustomAddButtonClick', evt);
    if (onActionbarAction) onActionbarAction(evt, 'add', null);
  };

  const onTableActionbarAction = (evt, type) => {
    // console.log('onTableActionbarAction', evt);
    if (onActionbarAction) onActionbarAction(evt, type);
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      if (!loading) {
        scrollTopBeforeLoading.current = container.scrollTop;
      }
    };
    if (!loading && tableContainerRef.current) {
      // console.log('Restoring scrollTop to:', scrollTopBeforeLoading.current);
      tableContainerRef.current.scrollTop = scrollTopBeforeLoading.current;
    }
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [loading]);
  // --------------------------------------------------
  //
  // end TABLE ACTIONBAR
  //
  // --------------------------------------------------

  return (
    <div className="fuel-table">
      {showActionbar && (
        <div className="table-actionbar">
          <div className="left-actionbar-container inline">
            {customAddButtonText && (
              <div className="inline">
                <Button className="custom-add-button" color="primary" variant="contained" onClick={onCustomAddButtonClick}>
                  {customAddButtonText}
                </Button>
              </div>
            )}
            {customAddButtonText && customActionbarHtml && (
              <div className="inline">
                <div className="hdivider" />
              </div>
            )}
            {customActionbarHtml && <div className="inline">{customActionbarHtml}</div>}
          </div>
          <div className="right-actionbar-container">
            {!hideSearchControl && showDateFilter && (
              <div className="date-controls-layout inline">
                <div className="date-picker-wrapper">
                  <DatePicker
                    onChange={(v) => onDateInputChange('date', v)}
                    label="Date"
                    value={fromDate}
                    InputProps={{
                      className: 'date-input',
                    }}
                    renderInput={(params) => (
                      <TextField
                        size="small"
                        InputProps={{
                          className: 'date-input',
                        }}
                        {...params}
                      />
                    )}
                  />
                </div>
                <div className="date-picker-dash">&mdash;</div>
                <div className="range-wrapper">
                  <FuelAutoComplete
                    id="create-daterange-select"
                    items={dateRangeOptions}
                    itemText="name"
                    itemKey="id"
                    labelText="Range"
                    multiSelect={false}
                    labelPaddingTop="-5px"
                    defaultValue={dateRange}
                    onChange={(value) => onDateInputChange('dateRange', value)}
                    placeHolderText=""
                  />
                </div>
              </div>
            )}
            {!hideSearchControl && (
              <div className="search-input-wrapper inline">
                <FuelSearchInput
                  defaultValue={searchDefaultValue}
                  onChange={onSearchTextChanged}
                  onSearch={(value) => onWordSearch(value)}
                />
              </div>
            )}
            {!hideActionbarButtons && (
              <div className="inline">
                <div className="hdivider" />
                <div className="right-actionbar-buttons" id="asset-listing-header">
                  {!hideAddButton && !customAddButtonText && (
                    <div title="Add Item" className="element">
                      <i
                        test-id="add-icon"
                        aria-hidden="true"
                        className="icon material-icons"
                        onClick={(evt) => onTableActionbarAction(evt, 'add')}
                      >
                        add
                      </i>
                    </div>
                  )}
                  {permissions && permissions.has(Permission.EditAsset) && !hideSelectionButtons && mySelectedRows.length > 0 && (
                    <div className="inline">
                      {entityType === FuelEntityType.Clip && (
                        <div title="Edit Multiple" className="element">
                          <i
                            test-id="edit-multiple-icon"
                            aria-hidden="true"
                            className="icon material-icons"
                            onClick={(evt) => onTableActionbarAction(evt, 'edit')}
                          >
                            mode_edit
                          </i>
                        </div>
                      )}
                      {/* {entityType === FuelEntityType.Channel && (
                        <div title="Add Favorites" className="element">
                          <BookmarkIcon
                            // sx={{ transform: 'rotate(-90deg)' }}
                            className="icon"
                            onClick={(evt) => onTableActionbarAction(evt, 'addFavorites')}
                          />
                        </div>
                      )} */}
                      {entityType === FuelEntityType.Channel && (
                        <div title="Show EPG Viewer" className="element">
                          <ViewKanbanIcon
                            sx={{ transform: 'rotate(-90deg)' }}
                            className="icon"
                            onClick={(evt) => onTableActionbarAction(evt, 'epgViewer')}
                          />
                        </div>
                      )}

                      {entityType === FuelEntityType.Input && (
                        <div title="Show Schedule Inputs" className="element">
                          <CalendarMonthIcon
                            // sx={{ transform: 'rotate(-90deg)' }}
                            className="icon"
                            onClick={(evt) => onTableActionbarAction(evt, 'scheduleDisplay')}
                          />
                        </div>
                      )}
                      {entityType !== FuelEntityType.LiveProgram && entityType !== FuelEntityType.Input && (
                        <div title="Delete Multiple" className="element">
                          <i
                            test-id="delete-multiple-icon"
                            aria-hidden="true"
                            className="icon material-icons"
                            onClick={(evt) => onTableActionbarAction(evt, 'delete')}
                          >
                            delete
                          </i>
                        </div>
                      )}
                    </div>
                  )}
                  <div title="Refresh" className="element">
                    <i
                      test-id="refresh-icon"
                      aria-hidden="true"
                      className="icon material-icons"
                      onClick={(evt) => onTableActionbarAction(evt, 'refresh')}
                    >
                      refresh
                    </i>
                  </div>
                  {!hideThumbnailButtons && (
                    <div className="inline">
                      <div title="Thumbnail View" className="element">
                        <i
                          test-id="thumbnail-view-icon"
                          aria-hidden="true"
                          className="icon material-icons"
                          onClick={(evt) => onTableActionbarAction(evt, 'view_list')}
                        >
                          view_list
                        </i>
                      </div>
                      <div title="List View" className="element">
                        <i
                          test-id="list-view-icon"
                          aria-hidden="true"
                          className="icon material-icons"
                          onClick={(evt) => onTableActionbarAction(evt, 'view_headline')}
                        >
                          view_headline
                        </i>
                      </div>
                    </div>
                  )}
                  {showExportButton && (
                    <div className="inline">
                      <div title="Export" className="element">
                        <i
                          test-id="fuel-table-export-icon"
                          aria-hidden="true"
                          className="icon material-icons"
                          onClick={(evt) => onTableActionbarAction(evt, 'export')}
                        >
                          file_download
                        </i>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <div className={classSet({ 'table-wrapper': true, 'with-pagination': !hidePagination, 'with-header': showActionbar })}>
        <TableContainer className="table-container" id={id} ref={tableContainerRef}>
          <Table
            stickyHeader
            {...{
              style: {
                borderCollapse: 'collapse',
                width: table.getCenterTotalSize(),
                minWidth: '100%',
                border: 'none',
              },
            }}
            id={id}
          >
            <TableHead>
              {table.getHeaderGroups().map((headerGroup, headerIndex) => (
                <TableRow key={headerIndex}>
                  {headerGroup.headers.map((header, headerCellIndex) => (
                    <StyledTableCell
                      key={headerCellIndex}
                      {...{
                        colSpan: header.colSpan,
                        style: {
                          width: header.column.columnDef.width,
                          maxWidth: header.column.columnDef.width,
                          minWidth: header.column.columnDef.width,
                        },
                      }}
                      sx={{
                        padding: 0,
                        // borderRight: '1px solid white',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        height: '48px',
                        paddingLeft: header.column.columnDef.align === 'left' ? '8px' : '0px',
                        // color: header.isSorted ? 'rgba(0,0,0,.87)' : 'rgba(0,0,0,.54)',
                        textAlign: header.column.columnDef.align === 'left' ? 'left' : 'center',
                      }}
                      className={`${header.column.columnDef.className} align-${header.column.columnDef.align} ${
                        header.column.columnDef.sortable ? 'is-sortable' : ''
                      }`}
                    >
                      <div
                        {...{
                          className: header.column.columnDef.sortable ? 'fuel-table-header cursor-pointer' : 'fuel-table-header',
                          onClick: (event) => onHeaderClick(event, header.column),
                          // style: {
                          //   width: header.column.columnDef.width,
                          // },
                        }}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.columnDef.sortable && (
                          <IconButton className="sort-btn">
                            <ArrowDropUp
                              style={{ top: '-2px' }}
                              className={classSet({ active: header.column.getIsSorted() === 'asc' })}
                            />
                            <ArrowDropDown
                              style={{ top: '5px' }}
                              className={classSet({ active: header.column.getIsSorted() === 'desc' })}
                            />
                          </IconButton>
                        )}

                        {header.column.columnDef.icon && <>{header.column.columnDef.icon}</>}
                      </div>

                      {/* <div
                        {...{
                          onMouseDown: header.getResizeHandler(),
                          onTouchStart: header.getResizeHandler(),
                          // onClick: header.column.getToggleSortingHandler(),
                          className: `resizer ${header.column.getIsResizing() ? 'isResizing' : ''}  ${
                            header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                          }`,
                          style: {
                            transform:
                              columnResizeMode === 'onEnd' && header.column.getIsResizing()
                                ? `translateX(${table.getState().columnSizingInfo.deltaOffset}px)`
                                : '',
                          },
                        }}
                      /> */}
                    </StyledTableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>

            {(drop || table.getRowModel().rows.length > 0) && !loading ? (
              <FuelTableRows
                rows={table.getRowModel().rows}
                drag={drag}
                pull={pull}
                drop={drop}
                onDrag={onDrag}
                dropInPlace={dropInPlace}
                rowRefs={rowRefs}
                dragAndDropText={dragAndDropText}
                // latestLft={data}
              />
            ) : (
              <TableBody>
                <TableRow key={-1}>
                  <TableCell key={-1} colSpan={columns ? columns.length : 0} rowSpan={columns.length} sx={{ border: 'none' }}>
                    {!loading ? (
                      <Box>
                        <Typography sx={{ textAlign: 'center !important' }}>No data available</Typography>
                      </Box>
                    ) : (
                      <Box>
                        <Typography sx={{ textAlign: 'center !important' }}>loading...</Typography>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </div>
      {!hidePagination && (
        <div className="pagination-container">
          {paginationSummary && (
            <div
              className="pagination-stats"
              title={`Showing ${paginationSummary.from} - ${paginationSummary.to} of ${paginationSummary.total}`}
            >
              Showing {paginationSummary.from} &mdash; {paginationSummary.to} of {paginationSummary.total}
              {/* {paginationSummary.rowsPerPage === paginationSummary.total ? '+' : ''} */}
            </div>
          )}
          <div className="pagination-steps">
            {!loading && (
              <Pagination
                className="pagination-steps"
                size="small"
                siblingCount={0}
                boundaryCount={1}
                count={paginationDetail.pages}
                page={paginationDetail.page}
                variant="outlined"
                shape="rounded"
                color="primary"
                onChange={handlePageChange}
                sx={{ marginBottom: '20px !important' }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FuelTable;
